import { BaseCanvasBasedPlayer } from './BaseCanvasBasedPlayer';
import VideoSettings from '../VideoSettings';
import Size from '../Size';
import { DisplayInfo } from '../DisplayInfo';
import H264Parser from 'h264-converter/dist/h264-parser';
import NALU from 'h264-converter/dist/util/NALU';
import ScreenInfo from '../ScreenInfo';
import Rect from '../Rect';

type ParametersSubSet = {
    codec: string;
    width: number;
    height: number;
};

type NalUnitInfo = {
    type: number;
    startCodeLength: number;
    nalStart: number;
    nalEnd: number;
};

function toHex(value: number) {
    return value.toString(16).padStart(2, '0').toUpperCase();
}

export class WebCodecsPlayer extends BaseCanvasBasedPlayer {
    public static readonly storageKeyPrefix = 'WebCodecsPlayer';
    public static readonly playerFullName = 'WebCodecs';
    public static readonly playerCodeName = 'webcodecs';

    public static readonly preferredVideoSettings: VideoSettings = new VideoSettings({
        lockedVideoOrientation: -1,
        bitrate: 524288,
        maxFps: 30,
		zoomsize:0.4,
        iFrameInterval: 5,
        bounds: new Size(480, 480),
        sendFrameMeta: false,
    });

    public static isSupported(): boolean {
        if (typeof VideoDecoder !== 'function' || typeof VideoDecoder.isConfigSupported !== 'function') {
            return false;
        }

        // FIXME: verify support
        // const result = await VideoDecoder.isConfigSupported();
        return true;
    }

    private static parseSPS(data: Uint8Array): ParametersSubSet {
        const {
            profile_idc,
            constraint_set_flags,
            level_idc,
            pic_width_in_mbs_minus1,
            frame_crop_left_offset,
            frame_crop_right_offset,
            frame_mbs_only_flag,
            pic_height_in_map_units_minus1,
            frame_crop_top_offset,
            frame_crop_bottom_offset,
            sar,
        } = H264Parser.parseSPS(data);

        const sarScale = sar[0] / sar[1];
        const codec = `avc1.${[profile_idc, constraint_set_flags, level_idc].map(toHex).join('')}`;
        const width = Math.ceil(
            ((pic_width_in_mbs_minus1 + 1) * 16 - frame_crop_left_offset * 2 - frame_crop_right_offset * 2) * sarScale,
        );
        const height =
            (2 - frame_mbs_only_flag) * (pic_height_in_map_units_minus1 + 1) * 16 -
            (frame_mbs_only_flag ? 2 : 4) * (frame_crop_top_offset + frame_crop_bottom_offset);
        return { codec, width, height };
    }

    public readonly supportsScreenshot = true;
    private context: CanvasRenderingContext2D;
    private decoder: VideoDecoder;
    private buffer: ArrayBuffer | undefined;
    private hadIDR = false;
    private bufferedSPS = false;
    private bufferedPPS = false;
    private chunkTimestamp = 0;
    private configuredCodec?: string;
    private configuredWidth = 0;
    private configuredHeight = 0;

    constructor(udid: string, displayInfo?: DisplayInfo, name = WebCodecsPlayer.playerFullName) {
        super(udid, displayInfo, name, WebCodecsPlayer.storageKeyPrefix);
        const context = this.tag.getContext('2d');
        if (!context) {
            throw Error('Failed to get 2d context from canvas');
        }
        this.context = context;
        this.decoder = this.createDecoder();
    }

    private createDecoder(): VideoDecoder {
        return new VideoDecoder({
            output: (frame) => {
                this.onFrameDecoded(0, 0, frame);
            },
            error: (error: DOMException) => {
                console.error(error, `code: ${error.code}`);
                this.stop();
            },
        });
    }

    private getStartCodeLength(data: Uint8Array): number {
        if (data.length > 4 && data[0] === 0 && data[1] === 0 && data[2] === 0 && data[3] === 1) {
            return 4;
        }
        if (data.length > 3 && data[0] === 0 && data[1] === 0 && data[2] === 1) {
            return 3;
        }
        return 0;
    }

    private findNalUnits(data: Uint8Array): NalUnitInfo[] {
        const units: NalUnitInfo[] = [];
        let index = 0;
        while (index < data.length - 3) {
            const startCodeLength = this.getStartCodeLength(data.subarray(index));
            if (!startCodeLength) {
                index += 1;
                continue;
            }
            const nalStart = index + startCodeLength;
            let nextIndex = nalStart;
            while (nextIndex < data.length - 3) {
                if (this.getStartCodeLength(data.subarray(nextIndex))) {
                    break;
                }
                nextIndex += 1;
            }
            if (nalStart < data.length) {
                units.push({
                    type: data[nalStart] & 31,
                    startCodeLength,
                    nalStart,
                    nalEnd: nextIndex >= data.length - 3 ? data.length : nextIndex,
                });
            }
            index = nextIndex;
        }
        return units;
    }

    private configureDecoder(codec: string, width: number, height: number): void {
        const needsReconfigure =
            this.decoder.state !== 'configured' ||
            this.configuredWidth !== width ||
            this.configuredHeight !== height;
        if (!needsReconfigure) {
            return;
        }
        const config: VideoDecoderConfig = {
            codec,
            optimizeForLatency: true,
        } as VideoDecoderConfig;
        console.info('[WebCodecsPlayer] configure decoder', {
            codec,
            width,
            height,
        });
        this.decoder.configure(config);
        this.configuredCodec = codec;
        this.configuredWidth = width;
        this.configuredHeight = height;
        this.chunkTimestamp = 0;
    }

    protected addToBuffer(data: Uint8Array): Uint8Array {
        let array: Uint8Array;
        if (this.buffer) {
            array = new Uint8Array(this.buffer.byteLength + data.byteLength);
            array.set(new Uint8Array(this.buffer));
            array.set(new Uint8Array(data), this.buffer.byteLength);
        } else {
            array = data;
        }
        this.buffer = array.buffer;
        return array;
    }

    protected scaleCanvas(width: number, height: number): void {
        const sizeChanged = this.videoWidth !== width || this.videoHeight !== height;
        this.videoWidth = width;
        this.videoHeight = height;
        if (!sizeChanged && this.getScreenInfo()) {
            return;
        }
        const screenInfo = new ScreenInfo(new Rect(0, 0, width, height), new Size(width, height), 0);
        this.emit('input-video-resize', screenInfo);
        this.setScreenInfo(screenInfo);

        this.initCanvas(width, height);
        this.tag.style.transform = '';
        this.tag.style.transformOrigin = '';
    }

    protected decode(data: Uint8Array): void {
        if (!data || data.length < 4) {
            return;
        }
        const nalUnits = this.findNalUnits(data);
        if (!nalUnits.length) {
            return;
        }

        let packetHasSPS = false;
        let packetHasPPS = false;
        let packetHasIDR = false;
        let packetHasOnlySeiWithoutConfig = true;

        for (const unit of nalUnits) {
            if (unit.type === NALU.SPS) {
                packetHasSPS = true;
                packetHasOnlySeiWithoutConfig = false;
                const { codec, width, height } = WebCodecsPlayer.parseSPS(
                    data.subarray(unit.nalStart, unit.nalEnd),
                );
                this.scaleCanvas(width, height);
                this.configureDecoder(codec, width, height);
            } else if (unit.type === NALU.PPS) {
                packetHasPPS = true;
                packetHasOnlySeiWithoutConfig = false;
            } else if (unit.type === NALU.IDR) {
                packetHasIDR = true;
                packetHasOnlySeiWithoutConfig = false;
            } else if (unit.type !== NALU.SEI) {
                packetHasOnlySeiWithoutConfig = false;
            }
        }

        if (packetHasOnlySeiWithoutConfig && (!this.bufferedSPS || !this.bufferedPPS)) {
            return;
        }

        this.bufferedSPS = this.bufferedSPS || packetHasSPS;
        this.bufferedPPS = this.bufferedPPS || packetHasPPS;

        const array = this.addToBuffer(data);
        this.hadIDR = this.hadIDR || packetHasIDR;
        if (array && this.decoder.state === 'configured' && this.hadIDR) {
            this.buffer = undefined;
            this.bufferedPPS = false;
            this.bufferedSPS = false;
            this.chunkTimestamp += 1;
            this.decoder.decode(
                new EncodedVideoChunk({
                    type: packetHasIDR ? 'key' : 'delta',
                    timestamp: this.chunkTimestamp,
                    data: array.buffer,
                }),
            );
            return;
        }
    }

    protected drawDecoded = (): void => {
        if (this.receivedFirstFrame) {
            const data = this.decodedFrames.shift();
            if (data) {
                const frame: VideoFrame = data.frame;
                this.context.drawImage(frame, 0, 0);
                frame.close();
            }
        }
        if (this.decodedFrames.length) {
            this.animationFrameId = requestAnimationFrame(this.drawDecoded);
        } else {
            this.animationFrameId = undefined;
        }
    };

    protected dropFrame(frame: VideoFrame): void {
        frame.close();
    }

    public getFitToScreenStatus(): boolean {
        return false;
    }

    public getPreferredVideoSetting(): VideoSettings {
        return WebCodecsPlayer.preferredVideoSettings;
    }

    public loadVideoSettings(): VideoSettings {
        return WebCodecsPlayer.loadVideoSettings(this.udid, this.displayInfo);
    }

    protected needScreenInfoBeforePlay(): boolean {
        return false;
    }

    public stop(): void {
        super.stop();
        if (this.decoder.state === 'configured') {
            this.decoder.close();
        }
        this.configuredCodec = undefined;
        this.configuredWidth = 0;
        this.configuredHeight = 0;
    }
}
